/**
 * 工作区报告管理 Store
 *
 * 管理工作区报告的生成、更新、下载等操作
 *
 * @module clinical-record/infrastructure/stores/reportStore
 */

import { defineStore } from "pinia";
import { computed, ref } from "vue";
import {
  deleteWorkspaceReport,
  downloadWorkspaceReport,
  fetchWorkspaceDocumentTypes,
  fetchWorkspaceReport,
  fetchWorkspaceReports,
  saveWorkspaceReport,
  updateWorkspaceReport,
  type WorkspaceDocumentTypeInfo,
  type WorkspaceReportDto,
} from "@/api/workspaceReportApi";
import {
  mergePatientInfo,
  formatAgeDisplay,
  normalizeGender,
} from "@clinical-record/shared/utils/patientInfoNormalizer";
import { useMedicalStore } from "./recordStore";
import { usePatientManagementStore } from "@patient-management/patient/infrastructure/patientStore";
import { isRecordPatientIdentityAligned } from "@clinical-record/shared/domain/currentMedicalContext";
import type {
  WorkspaceReport,
  WorkspaceReviewStatus,
  ConversationData,
  CaptureQualityReportParams,
  CaptureConstitutionReportParams,
} from "./reportTypes";
import type { TabName } from "./chatStore";
import type { RequestContext } from "@shared/http/requestContext";
import {
  REPORT_TITLE_BY_KEY,
  fallbackOptionToReportKey,
  optionToDocumentLabel,
  normalizeLibrarySection,
  normalizeSourceKind,
  normalizeReviewStatus,
  normalizeState,
  parseTime,
  sanitizeFileNamePart,
  pickPlaceholderName,
} from "./constants";

// ==================== 工具函数 ====================

/**
 * 创建空报告
 */
function createEmptyReport(key: string): WorkspaceReport {
  const title = REPORT_TITLE_BY_KEY[key] || "诊疗报告";
  return {
    id: "",
    key,
    reportKey: key,
    documentType: "",
    templateKey: "",
    title,
    state: "待生成",
    content: "",
    sourceTab: "",
    sourceOption: "",
    sourceHistory: [],
    updatedAt: null,
    patientName: "",
    documentTypeLabel: title,
    docxFileName: "",
    docxError: "",
    librarySection: "ai_report",
    recordId: "",
    patientId: "",
    visitDate: "",
    sourceKind: "ai_generated",
    reviewStatus: "draft",
    version: 1,
    revisionHistory: [],
    contentLoaded: true,
  };
}

/** 列表分页大小（与后端 limit 上限一致） */
const REPORT_PAGE_SIZE = 200;
/** 列表最多拉取页数，防止异常 total 导致无限翻页 */
const REPORT_MAX_PAGES = 10;

/**
 * 解析患者名称
 */
function resolvePatientName(
  seed: number,
  patientStore: any,
  medicalStore: any,
): string {
  const patient = patientStore.currentPatient as any;
  const record = medicalStore.currentMedicalRecord as any;
  const alignedPatient = isRecordPatientIdentityAligned(patient, record)
    ? patient
    : null;
  const rawName =
    record?.patient_name ||
    record?.patientName ||
    alignedPatient?.name ||
    record?.name ||
    "";
  return sanitizeFileNamePart(rawName) || pickPlaceholderName(seed);
}

/**
 * 字符化当前病历
 */
function stringifyCurrentMedicalRecord(record: Record<string, any>): string {
  // 使用 patientInfoNormalizer 统一处理患者信息
  const patientInfo = mergePatientInfo(null, record);

  const fields: Array<[string, string]> = [
    ["姓名", patientInfo.name || ""],
    ["性别", normalizeGender(patientInfo.gender)],
    ["年龄", formatAgeDisplay(patientInfo.age)],
    ["就诊日期", record.visit_date || ""],
    ["主诉", record.chief_complaint || ""],
    ["现病史", record.present_illness || ""],
    ["既往史", record.past_history || ""],
    ["过敏史", record.allergy_history || ""],
    [
      "体格检查",
      record.physical_examination_text || record.physical_examination || "",
    ],
    ["实验室检查", record.lab_results_text || ""],
    ["影像检查", record.imaging_results_text || ""],
    [
      "西医诊断",
      record.western_diagnosis_text || record.western_diagnosis || "",
    ],
    ["中医诊断", record.tcm_diagnosis || ""],
    ["中医证候", record.tcm_pattern_text || ""],
    ["西医治疗计划", record.western_treatment_plan || ""],
    ["中医治疗计划", record.tcm_treatment_plan || ""],
    ["处置意见", record.management_plan || ""],
    ["注意事项", record.precautions || ""],
  ];
  return fields
    .filter(([, value]) => String(value || "").trim())
    .map(([label, value]) => `【${label}】\n${String(value).trim()}`)
    .join("\n\n");
}

/**
 * 按时间倒序排序
 */
function sortByTimeDesc(list: WorkspaceReport[]): WorkspaceReport[] {
  return [...list].sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
}

function makePendingKey(reportKey: string, seed = Date.now()): string {
  return `pending_${reportKey}_${seed}`.replace(/[^\w-]/g, "_");
}

// ==================== Store 定义 ====================

export const useWorkspaceReportStore = defineStore("workspaceReport", () => {
  const patientStore = usePatientManagementStore();
  const medicalStore = useMedicalStore();

  const documentTypes = ref<WorkspaceDocumentTypeInfo[]>([]);
  const loaded = ref(false);
  const loading = ref(false);

  const reports = ref<Record<string, WorkspaceReport>>({
    initialDiagnosisReport: createEmptyReport("initialDiagnosisReport"),
    clinicalAnalysisReport: createEmptyReport("clinicalAnalysisReport"),
    admissionRecord: createEmptyReport("admissionRecord"),
    constitutionReport: createEmptyReport("constitutionReport"),
    treatmentPlanReport: createEmptyReport("treatmentPlanReport"),
    followupPlanReport: createEmptyReport("followupPlanReport"),
    recordQualityReport: createEmptyReport("recordQualityReport"),
    chronicManagementReport: createEmptyReport("chronicManagementReport"),
  });

  // ==================== 计算属性 ====================

  const reportList = computed(() => Object.values(reports.value));
  const generatedReports = computed(() =>
    reportList.value.filter((report) => report.state !== "待生成"),
  );
  const officialRecords = computed(() =>
    sortByTimeDesc(
      generatedReports.value.filter(
        (report) => report.librarySection === "official_record",
      ),
    ),
  );
  const qualityReports = computed(() =>
    sortByTimeDesc(
      generatedReports.value.filter(
        (report) => report.librarySection === "quality_report",
      ),
    ),
  );
  const aiReports = computed(() =>
    sortByTimeDesc(
      generatedReports.value.filter(
        (report) => report.librarySection === "ai_report",
      ),
    ),
  );

  // ==================== 工具方法 ====================

  /**
   * 获取报告
   */
  function getReport(key: string): WorkspaceReport {
    const exact = reports.value[key];
    if (exact && exact.state !== "待生成") return exact;

    const latest = sortByTimeDesc(
      Object.values(reports.value).filter(
        (report) => report.reportKey === key && report.state !== "待生成",
      ),
    )[0];
    if (latest) return latest;

    if (exact) return exact;

    if (!reports.value[key]) {
      reports.value[key] = createEmptyReport(key);
    }
    return reports.value[key];
  }

  /**
   * 应用 DTO 到报告
   */
  function applyDto(dto: WorkspaceReportDto): WorkspaceReport {
    const key = dto.id || dto.report_key;
    const existing = getReport(key);
    const contentLoaded = dto.content_loaded !== false;
    // 轻量列表刷新不得覆盖已补全的正文（同一版本下正文不变）
    const keepLoadedBody =
      !contentLoaded &&
      existing.id === dto.id &&
      existing.contentLoaded &&
      existing.version === (dto.version || 1);
    reports.value[key] = {
      ...existing,
      id: dto.id,
      key,
      reportKey: dto.report_key,
      documentType: dto.document_type,
      templateKey: dto.template_key || "",
      title: dto.title,
      state: normalizeState(dto.state),
      content: keepLoadedBody ? existing.content : dto.content || "",
      sourceTab: dto.source_tab as TabName | "",
      sourceOption: dto.source_option || "",
      sourceHistory: keepLoadedBody
        ? existing.sourceHistory
        : ((dto.source_history || []) as any[]),
      contentLoaded: contentLoaded || keepLoadedBody,
      updatedAt: parseTime(dto.updated_at),
      patientName: dto.patient_name || "",
      documentTypeLabel: dto.document_type_label || dto.title,
      docxFileName: dto.docx_file_name || "",
      docxError: dto.docx_error || "",
      librarySection: normalizeLibrarySection(dto.library_section),
      recordId: dto.record_id || "",
      patientId: dto.patient_id || "",
      visitDate: dto.visit_date || "",
      sourceKind: normalizeSourceKind(dto.source_kind),
      reviewStatus: normalizeReviewStatus(dto.review_status),
      version: dto.version || 1,
      revisionHistory: keepLoadedBody
        ? existing.revisionHistory
        : dto.revision_history || [],
    };
    return reports.value[key];
  }

  /**
   * 解析类型
   */
  function resolveType(
    tab: TabName,
    option: string,
    reportKey: string,
  ): WorkspaceDocumentTypeInfo | undefined {
    return (
      documentTypes.value.find(
        (item) => item.source_tab === tab && item.source_option === option,
      ) || documentTypes.value.find((item) => item.report_key === reportKey)
    );
  }

  /**
   * 选项到报告键
   */
  function optionToReportKey(tab: TabName, option: string): string {
    return (
      resolveType(tab, option, "")?.report_key ||
      fallbackOptionToReportKey(tab, option)
    );
  }

  /**
   * 选项到文档类型
   */
  function optionToDocumentType(
    tab: TabName,
    option: string,
    reportKey: string,
  ): string {
    return resolveType(tab, option, reportKey)?.type_key || reportKey;
  }

  // ==================== Actions ====================

  /**
   * 加载文档类型
   */
  async function loadDocumentTypes(): Promise<WorkspaceDocumentTypeInfo[]> {
    const res = await fetchWorkspaceDocumentTypes();
    documentTypes.value = res.types || [];
    return documentTypes.value;
  }

  /**
   * 加载报告
   */
  async function loadReports(): Promise<void> {
    loading.value = true;
    try {
      if (documentTypes.value.length === 0) {
        await loadDocumentTypes();
      }
      // 列表只取元数据（后端投影排除正文），正文在打开时按需补全
      for (let page = 0; page < REPORT_MAX_PAGES; page += 1) {
        const res = await fetchWorkspaceReports({
          include_content: false,
          offset: page * REPORT_PAGE_SIZE,
          limit: REPORT_PAGE_SIZE,
        });
        const items = res.reports || [];
        for (const report of items) {
          applyDto(report);
        }
        const total = res.total ?? items.length;
        if (
          items.length < REPORT_PAGE_SIZE ||
          (page + 1) * REPORT_PAGE_SIZE >= total
        ) {
          break;
        }
      }
      loaded.value = true;
    } finally {
      loading.value = false;
    }
  }

  const hydrating = new Map<string, Promise<WorkspaceReport>>();

  /**
   * 确保报告正文已加载：轻量列表项经详情接口补全 content/source_history。
   * 同一报告的并发调用共享同一请求。
   */
  async function ensureReportContent(key: string): Promise<WorkspaceReport> {
    const report = getReport(key);
    if (report.contentLoaded || !report.id) return report;
    const inflight = hydrating.get(report.id);
    if (inflight) return inflight;
    const request = fetchWorkspaceReport(report.id)
      .then((dto) => applyDto(dto))
      .finally(() => hydrating.delete(report.id));
    hydrating.set(report.id, request);
    return request;
  }

  /**
   * 确保已加载
   */
  async function ensureLoaded(): Promise<void> {
    if (loaded.value || loading.value) return;
    await loadReports();
  }

  /**
   * 从聊天捕获报告
   */
  async function captureFromChat(
    tab: TabName,
    option: string,
    history: any[],
    requestContext?: RequestContext,
  ): Promise<WorkspaceReport | null> {
    const assistant =
      [...history]
        .reverse()
        .find(
          (msg) =>
            msg.role === "assistant" &&
            (msg.sourceMarkdown || msg.plainText || msg.content)?.trim() &&
            msg.metadata?.selectedOption === option,
        ) ||
      [...history]
        .reverse()
        .find(
          (msg) =>
            msg.role === "assistant" &&
            (msg.sourceMarkdown || msg.plainText || msg.content)?.trim(),
        );
    if (!assistant) return null;
    const sourceContent =
      assistant?.plainText ||
      assistant?.sourceMarkdown ||
      assistant?.content ||
      "";
    if (!sourceContent.trim()) return null;

    const assistantIndex = history.lastIndexOf(assistant);
    const previousUser = history
      .slice(0, assistantIndex)
      .reverse()
      .find(
        (msg) =>
          msg.role === "user" &&
          msg.content?.trim() &&
          (!msg.metadata?.selectedOption ||
            msg.metadata.selectedOption === option),
      );
    if (documentTypes.value.length === 0) {
      try {
        await loadDocumentTypes();
      } catch {
        // 类型接口异常时使用本地兼容映射，保证保存动作可完成。
      }
    }

    const sourceHistory = [previousUser, assistant].filter(Boolean) as any[];
    const reportKey = optionToReportKey(tab, option);
    const documentTypeLabel = optionToDocumentLabel(tab, option, reportKey);
    const documentType = optionToDocumentType(tab, option, reportKey);
    const resolvedType = resolveType(tab, option, reportKey);
    const librarySection = normalizeLibrarySection(
      resolvedType?.library_section,
    );
    const updatedAt = Date.now();
    const patientName = resolvePatientName(
      updatedAt,
      patientStore,
      medicalStore,
    );
    const pendingKey = makePendingKey(reportKey, updatedAt);

    const existing = getReport(reportKey);

    reports.value[pendingKey] = {
      ...existing,
      id: "",
      key: pendingKey,
      reportKey,
      documentType,
      title: documentTypeLabel,
      state: "生成中",
      content: sourceContent,
      sourceTab: tab,
      sourceOption: option,
      sourceHistory,
      updatedAt,
      patientName,
      documentTypeLabel,
      docxError: "",
      librarySection,
      sourceKind: "ai_generated",
      reviewStatus: "draft",
      version: existing.version || 1,
    };

    try {
      const saved = await saveWorkspaceReport(
        {
          report_key: reportKey,
          document_type: documentType,
          title: documentTypeLabel,
          document_type_label: documentTypeLabel,
          content: sourceContent,
          patient_name: patientName,
          source_tab: tab,
          source_option: option,
          source_history: sourceHistory,
          library_section: librarySection,
          source_kind: "ai_generated",
          review_status: "draft",
        },
        requestContext,
      );
      const applied = applyDto(saved);
      delete reports.value[pendingKey];
      return applied;
    } catch (error: unknown) {
      reports.value[pendingKey] = {
        ...reports.value[pendingKey],
        state: "生成失败",
        docxError: error instanceof Error ? error.message : "文书保存失败",
      };
    }

    return reports.value[pendingKey];
  }

  /**
   * 更新报告内容
   */
  async function updateReportContent(
    key: string,
    content: string,
    options: { editReason?: string; reviewStatus?: WorkspaceReviewStatus } = {},
  ): Promise<WorkspaceReport> {
    const report = getReport(key);
    reports.value[key] = {
      ...report,
      content,
      state: "生成中",
      updatedAt: Date.now(),
      docxError: "",
    };
    try {
      const saved = report.id
        ? await updateWorkspaceReport(report.id, content, {
            edit_reason: options.editReason || "",
            review_status: options.reviewStatus || "",
          })
        : await saveWorkspaceReport({
            report_key: report.reportKey || key,
            document_type: report.documentType || key,
            template_key: report.templateKey || "",
            title: report.title,
            document_type_label: report.documentTypeLabel || report.title,
            content,
            patient_name:
              report.patientName ||
              resolvePatientName(Date.now(), patientStore, medicalStore),
            source_tab: report.sourceTab,
            source_option: report.sourceOption,
            source_history: report.sourceHistory,
            library_section: report.librarySection,
            record_id: report.recordId,
            patient_id: report.patientId,
            visit_date: report.visitDate,
            source_kind: report.sourceKind,
            review_status: options.reviewStatus || report.reviewStatus,
            version: report.version,
            edit_reason: options.editReason || "",
          });
      return applyDto(saved);
    } catch (error: unknown) {
      reports.value[key] = {
        ...reports.value[key],
        state: "生成失败",
        docxError: error instanceof Error ? error.message : "文书更新失败",
      };
      return reports.value[key];
    }
  }

  /**
   * 捕获当前病历
   */
  async function captureCurrentMedicalRecord(
    documentType = "outpatient_record",
    editReason = "由当前病历生成正式病历文书快照",
  ): Promise<WorkspaceReport | null> {
    const record = medicalStore.currentMedicalRecord as any;
    if (!record) return null;
    const recordId = String(record._id || record.id || `local-${Date.now()}`);
    const patientId = String(record.patient_id || record.patientId || "");
    const visitDate = String(record.visit_date || "");
    const updatedAt = Date.now();
    const reportKey = `officialRecord_${recordId}_${documentType}`.replace(
      /[^\w-]/g,
      "_",
    );
    const key = `${reportKey}_${updatedAt}`.replace(/[^\w-]/g, "_");
    const documentTypeLabel =
      documentType === "medical_record_snapshot" ? "病历快照" : "门诊病历";
    const patientName =
      sanitizeFileNamePart(record.patient_name || record.name || "") ||
      resolvePatientName(updatedAt, patientStore, medicalStore);
    const content = stringifyCurrentMedicalRecord(record);
    if (!content.trim()) return null;

    reports.value[key] = {
      ...createEmptyReport(key),
      key,
      reportKey,
      documentType,
      title: documentTypeLabel,
      state: "生成中",
      content,
      sourceTab: "medicalWriting",
      sourceOption: documentTypeLabel,
      sourceHistory: [],
      updatedAt,
      patientName,
      documentTypeLabel,
      librarySection: "official_record",
      recordId,
      patientId,
      visitDate,
      sourceKind: "current_medical_record",
      reviewStatus: "reviewed",
      version: 1,
      docxError: "",
    };

    try {
      const saved = await saveWorkspaceReport({
        report_key: reportKey,
        document_type: documentType,
        title: documentTypeLabel,
        document_type_label: documentTypeLabel,
        content,
        patient_name: patientName,
        source_tab: "medicalRecord",
        source_option: documentTypeLabel,
        source_history: [],
        library_section: "official_record",
        record_id: recordId,
        patient_id: patientId,
        visit_date: visitDate,
        source_kind: "current_medical_record",
        review_status: "reviewed",
        version: 1,
        edit_reason: editReason,
      });
      const applied = applyDto(saved);
      delete reports.value[key];
      return applied;
    } catch (error: unknown) {
      reports.value[key] = {
        ...reports.value[key],
        state: "生成失败",
        docxError:
          error instanceof Error ? error.message : "正式病历文书保存失败",
      };
      return reports.value[key];
    }
  }

  /**
   * 将 AI 报告升格为正式病历
   */
  async function promoteAiReportToOfficialRecord(
    key: string,
    content: string,
    editReason: string,
  ): Promise<WorkspaceReport | null> {
    const report = await ensureReportContent(key);
    if (!report.content?.trim() && !content.trim()) return null;
    const updatedAt = Date.now();
    const officialKey = `official_${key}_${updatedAt}`.replace(/[^\w-]/g, "_");
    const officialReportKey = `official_${report.reportKey || key}`.replace(
      /[^\w-]/g,
      "_",
    );
    const documentType =
      report.documentType === "admission_record"
        ? "admission_record"
        : report.documentType || "outpatient_record";
    const documentTypeLabel =
      report.documentTypeLabel || report.title || "正式病历文书";

    reports.value[officialKey] = {
      ...createEmptyReport(officialKey),
      key: officialKey,
      reportKey: officialReportKey,
      documentType,
      title: documentTypeLabel,
      state: "生成中",
      content,
      sourceTab: report.sourceTab,
      sourceOption: report.sourceOption,
      sourceHistory: report.sourceHistory,
      updatedAt,
      patientName:
        report.patientName ||
        resolvePatientName(updatedAt, patientStore, medicalStore),
      documentTypeLabel,
      librarySection: "official_record",
      recordId: report.recordId,
      patientId: report.patientId,
      visitDate: report.visitDate,
      sourceKind: "ai_promoted",
      reviewStatus: "reviewed",
      version: 1,
      docxError: "",
    };

    try {
      const saved = await saveWorkspaceReport({
        report_key: officialReportKey,
        document_type: documentType,
        template_key: report.templateKey || "",
        title: documentTypeLabel,
        document_type_label: documentTypeLabel,
        content,
        patient_name: reports.value[officialKey].patientName,
        source_tab: report.sourceTab,
        source_option: report.sourceOption,
        source_history: report.sourceHistory,
        library_section: "official_record",
        record_id: report.recordId,
        patient_id: report.patientId,
        visit_date: report.visitDate,
        source_kind: "ai_promoted",
        review_status: "reviewed",
        version: 1,
        edit_reason: editReason,
      });
      const applied = applyDto(saved);
      delete reports.value[officialKey];
      return applied;
    } catch (error: unknown) {
      reports.value[officialKey] = {
        ...reports.value[officialKey],
        state: "生成失败",
        docxError: error instanceof Error ? error.message : "升格正式失败",
      };
      return reports.value[officialKey];
    }
  }

  /**
   * 重新生成 DOCX
   */
  async function regenerateDocx(key: string): Promise<WorkspaceReport | null> {
    const report = await ensureReportContent(key);
    if (!report.content?.trim()) return null;
    return updateReportContent(key, report.content);
  }

  /**
   * 删除报告
   */
  async function deleteReport(key: string): Promise<void> {
    const report = getReport(key);
    if (report.id) {
      await deleteWorkspaceReport(report.id);
    }
    if (reports.value[key]?.id) {
      delete reports.value[key];
      return;
    }
    reports.value[key] = createEmptyReport(key);
  }

  /**
   * 转换为对话数据
   */
  function toConversationData(report: WorkspaceReport): ConversationData {
    const history = report.sourceHistory.length
      ? report.sourceHistory.map((msg) =>
          msg.role === "assistant"
            ? {
                ...msg,
                content: report.content,
                plainText: report.content,
                sourceMarkdown: report.content,
              }
            : msg,
        )
      : [
          {
            role: "assistant" as const,
            content: report.content,
            timestamp: report.updatedAt || Date.now(),
          },
        ];
    return {
      tabName: report.sourceTab || "diagnosisTreatment",
      history,
      totalMessages: history.length,
      userMessages: history.filter((msg) => msg.role === "user").length,
      assistantMessages: history.filter((msg) => msg.role === "assistant")
        .length,
    };
  }

  /**
   * 下载报告
   */
  async function downloadReport(key: string): Promise<boolean> {
    const report = getReport(key);
    if (!report.id) return false;
    const blob = await downloadWorkspaceReport(report.id);
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = report.docxFileName || `${report.title || "诊疗报告"}.docx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(objectUrl);
    return true;
  }

  /**
   * 捕获质检报告
   */
  async function captureQualityReport(
    params: CaptureQualityReportParams,
  ): Promise<WorkspaceReport | null> {
    const { content, checkTypeLabel, qualityScore, issueCount, summary } =
      params;
    if (!content.trim()) return null;

    const reportKey = "recordQualityReport";
    const documentType = "record_quality_report";
    const documentTypeLabel = "病历质检报告";
    const updatedAt = Date.now();
    const patientName = resolvePatientName(
      updatedAt,
      patientStore,
      medicalStore,
    );
    const pendingKey = makePendingKey(reportKey, updatedAt);
    const existing = getReport(reportKey);

    // 构建结构化正文：摘要 + 评分 + 原文
    const headerLines = [
      `# 病历质检报告`,
      ``,
      `**检查类型**：${checkTypeLabel}`,
      qualityScore != null ? `**质量评分**：${qualityScore} 分` : "",
      issueCount != null ? `**发现问题**：${issueCount} 个` : "",
      summary ? `**摘要**：${summary}` : "",
      "",
      "---",
      "",
    ]
      .filter((line) => line !== null)
      .join("\n");
    const fullContent = headerLines + content;

    reports.value[pendingKey] = {
      ...existing,
      id: "",
      key: pendingKey,
      reportKey,
      documentType,
      title: documentTypeLabel,
      state: "生成中",
      content: fullContent,
      sourceTab: "basicHealthCare",
      sourceOption: checkTypeLabel,
      sourceHistory: [],
      updatedAt,
      patientName,
      documentTypeLabel,
      docxError: "",
      librarySection: "quality_report",
      sourceKind: "ai_generated",
      reviewStatus: "draft",
      version: existing.version || 1,
    };

    try {
      const saved = await saveWorkspaceReport({
        report_key: reportKey,
        document_type: documentType,
        title: documentTypeLabel,
        document_type_label: documentTypeLabel,
        content: fullContent,
        patient_name: patientName,
        source_tab: "basicHealthCare",
        source_option: checkTypeLabel,
        source_history: [],
        library_section: "quality_report",
        source_kind: "ai_generated",
        review_status: "draft",
      });
      const applied = applyDto(saved);
      delete reports.value[pendingKey];
      return applied;
    } catch (error: unknown) {
      reports.value[pendingKey] = {
        ...reports.value[pendingKey],
        state: "生成失败",
        docxError: error instanceof Error ? error.message : "质检报告保存失败",
      };
    }

    return reports.value[pendingKey];
  }

  /**
   * 捕获体质辨识报告
   */
  async function captureConstitutionReport(
    params: CaptureConstitutionReportParams,
  ): Promise<WorkspaceReport | null> {
    const { content, primaryName, sourceTab = "tcmHealthCare" } = params;
    if (!content.trim()) return null;

    const reportKey = "constitutionSurveyReport";
    const documentType = "constitution_survey_report";
    const documentTypeLabel = "中医体质辨识报告";
    const updatedAt = Date.now();
    const patientName = resolvePatientName(
      updatedAt,
      patientStore,
      medicalStore,
    );
    const pendingKey = makePendingKey(reportKey, updatedAt);
    const existing = getReport(reportKey);

    const headerLines = [
      `# 中医体质辨识报告`,
      "",
      primaryName ? `**主要体质**：${primaryName}` : "",
      `**调查时间**：${new Date().toLocaleString("zh-CN")}`,
      "",
      "---",
      "",
    ]
      .filter((line) => line !== "")
      .join("\n");
    const fullContent = headerLines + content;

    reports.value[pendingKey] = {
      ...existing,
      id: "",
      key: pendingKey,
      reportKey,
      documentType,
      title: documentTypeLabel,
      state: "生成中",
      content: fullContent,
      sourceTab: sourceTab,
      sourceOption: documentTypeLabel,
      sourceHistory: [],
      updatedAt,
      patientName,
      documentTypeLabel,
      docxError: "",
      librarySection: "ai_report",
      sourceKind: "ai_generated",
      reviewStatus: "draft",
      version: (existing.version || 1) + 1,
    };

    try {
      const saved = await saveWorkspaceReport({
        report_key: reportKey,
        document_type: documentType,
        title: documentTypeLabel,
        document_type_label: documentTypeLabel,
        content: fullContent,
        patient_name: patientName,
        source_tab: sourceTab,
        source_option: documentTypeLabel,
        source_history: [],
        library_section: "ai_report",
        source_kind: "ai_generated",
        review_status: "draft",
      });
      const applied = applyDto(saved);
      delete reports.value[pendingKey];
      return applied;
    } catch (error: unknown) {
      reports.value[pendingKey] = {
        ...reports.value[pendingKey],
        state: "生成失败",
        docxError: error instanceof Error ? error.message : "体质报告保存失败",
      };
    }

    return reports.value[pendingKey];
  }

  return {
    // 状态
    reports,
    documentTypes,
    loaded,
    loading,

    // 计算属性
    reportList,
    generatedReports,
    officialRecords,
    qualityReports,
    aiReports,

    // 操作方法
    getReport,
    loadReports,
    loadDocumentTypes,
    ensureLoaded,
    ensureReportContent,
    captureFromChat,
    captureQualityReport,
    captureConstitutionReport,
    captureCurrentMedicalRecord,
    promoteAiReportToOfficialRecord,
    toConversationData,
    downloadReport,
    regenerateDocx,
    updateReportContent,
    deleteReport,
  };
});
